import { useEffect, useState } from 'react';
import { useApp } from '../state/store';

/** Androids/Chromes installations-event (finns inte i TS standard-lib). */
type BeforeInstallPrompt = {
  preventDefault: () => void;
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};

export type InstallPlatform = 'ios' | 'android';

/** Visa modalen efter så här många genomförda sökningar (låt användaren testa först). */
const SHOW_AFTER_SEARCHES = 2;
const DISMISS_KEY = 'bilmat-install-dismissed'; // användaren tryckte "inte nu"
const SHOWN_KEY = 'bilmat-install-shown'; // modalen har visats (visa bara en gång)

/** Körs appen redan som installerad (hemskärm/standalone)? */
function isStandalone(): boolean {
  const nav = navigator as unknown as { standalone?: boolean };
  return nav.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
}

/** iOS Safari (där "Lägg till på hemskärmen" finns via dela-menyn). */
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iphone|ipad|ipod/i.test(ua);
  const safari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return ios && safari;
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    /* localStorage kan vara blockerat (privat läge) — strunta i det */
  }
}

export type InstallPromptState = {
  open: boolean;
  platform: InstallPlatform | null;
  install: () => void; // endast Android — öppnar native install-dialogen
  dismiss: () => void;
};

/**
 * Install-modal som dyker upp EFTER ett par genomförda sökningar (så användaren
 * hinner testa tjänsten först). Visas bara en gång, och inte om appen redan är
 * installerad eller om användaren avfärdat den.
 *  - iOS: guidad modal (Apple tillåter ingen programmatisk installation).
 *  - Android/Chrome: riktig ett-tryck-installation via `beforeinstallprompt`.
 */
export function useInstallPrompt(): InstallPromptState {
  const searchCount = useApp((s) => s.searchCount);
  const [platform, setPlatform] = useState<InstallPlatform | null>(null);
  const [deferred, setDeferred] = useState<BeforeInstallPrompt | null>(null);
  const [open, setOpen] = useState(false);

  // Plattformsdetektering + behörighet (en gång).
  useEffect(() => {
    if (isStandalone() || readFlag(DISMISS_KEY) || readFlag(SHOWN_KEY)) return;

    if (isIosSafari()) {
      setPlatform('ios');
      return;
    }

    const onBeforeInstall = (e: BeforeInstallPrompt): void => {
      e.preventDefault();
      setDeferred(e);
      setPlatform('android');
    };
    const listener = onBeforeInstall as unknown as (e: unknown) => void;
    window.addEventListener('beforeinstallprompt', listener);
    return () => window.removeEventListener('beforeinstallprompt', listener);
  }, []);

  // Öppna modalen när tröskeln nåtts (en gång).
  useEffect(() => {
    if (!open && platform && searchCount >= SHOW_AFTER_SEARCHES) {
      setOpen(true);
      writeFlag(SHOWN_KEY);
    }
  }, [open, platform, searchCount]);

  const dismiss = (): void => {
    writeFlag(DISMISS_KEY);
    setOpen(false);
  };

  const install = (): void => {
    if (!deferred) return;
    void deferred.prompt();
    void deferred.userChoice.finally(() => {
      setDeferred(null);
      setOpen(false);
    });
  };

  return { open, platform, install, dismiss };
}
